// The Traces facet of the /transcripts/$id Dossier (transcript-timeline.md):
// per trace a KeyValue summary, the TraceWaterfall, and the deep link to the
// full /traces/$id read model.

import type { components } from "../api/schema";
import { firstTraceId, traceTreeToWaterfallSpans } from "../lib/traces";
import { canonicalWorkingScopeSearch } from "../lib/workingScope";
import {
  EmptyState,
  Inline,
  InlineError,
  KeyValue,
  RouterLink,
  Spinner,
  Stack,
  Text,
  TraceWaterfall,
} from "../ui";

type TraceSpansResponse = components["schemas"]["TraceSpansResponse"];

export interface TranscriptTraceProps {
  activeSpanId?: string;
  error?: string | null;
  isPending?: boolean;
  onNodePick?: (nodeId: string) => void;
  selectedNodeId?: string;
  traces: TraceSpansResponse[];
}

export function TranscriptTrace({
  activeSpanId,
  error = null,
  isPending = false,
  onNodePick,
  selectedNodeId,
  traces,
}: TranscriptTraceProps) {
  return (
    <Stack gap={4}>
      {isPending ? (
        <Inline gap={2}>
          <Spinner label="Loading trace" />
          <Text variant="muted">Loading trace...</Text>
        </Inline>
      ) : null}
      {error ? <InlineError>{error}</InlineError> : null}
      {!isPending && !error && traces.length === 0 ? (
        <EmptyState title="No trace spans" />
      ) : null}
      {traces.map((trace, index) => {
        const traceId = firstTraceId(trace);
        const tree = trace.tree ?? [];
        return (
          <Stack gap={2} key={traceId ?? index.toString()}>
            <KeyValue
              rows={[
                { label: "Trace", value: traceId ?? "-" },
                { label: "Spans", value: trace.total_spans.toString() },
                { label: "Roots", value: tree.length.toString() },
              ]}
            />
            {tree.length === 0 ? (
              <EmptyState
                description="No root spans in this trace."
                title="No waterfall"
              />
            ) : (
              <TraceWaterfall
                activeSpanId={activeSpanId}
                selectedSpanId={selectedNodeId}
                spans={traceTreeToWaterfallSpans(tree)}
                onSpanSelect={(span) => {
                  onNodePick?.(span.id);
                }}
              />
            )}
            {traceId ? (
              <Inline>
                <RouterLink
                  params={{ id: traceId }}
                  search={(previous) => ({
                    ...canonicalWorkingScopeSearch(previous),
                    ...(activeSpanId === undefined
                      ? {}
                      : { span_id: activeSpanId }),
                  })}
                  to="/traces/$id"
                >
                  Open trace
                </RouterLink>
              </Inline>
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
}
